import { useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { TRexLogo } from "../components/TRexLogo";
import { LayoutDashboard, FileText, Image, Inbox, Users, LayoutPanelLeft, LogOut, ExternalLink, Building2, BookOpen, Search } from "lucide-react";

const nav = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard" },
  { to: "/admin/articles", label: "Articles", icon: FileText, testid: "admin-nav-articles" },
  { to: "/admin/docs", label: "Docs", icon: BookOpen, testid: "admin-nav-docs" },
  { to: "/admin/members", label: "Members", icon: Building2, testid: "admin-nav-members" },
  { to: "/admin/media", label: "Media", icon: Image, testid: "admin-nav-media" },
  { to: "/admin/contacts", label: "Contacts", icon: Inbox, testid: "admin-nav-contacts" },
  { to: "/admin/pages", label: "Pages", icon: LayoutPanelLeft, testid: "admin-nav-pages" },
  { to: "/admin/seo", label: "SEO", icon: Search, testid: "admin-nav-seo" },
  { to: "/admin/admins", label: "Admins", icon: Users, testid: "admin-nav-admins" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.body.classList.add("admin-body");
    return () => document.body.classList.remove("admin-body");
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="admin-shell min-h-screen bg-bone text-aubergine" data-testid="admin-layout">
      <aside className="fixed left-0 top-0 z-30 hidden lg:flex flex-col h-screen w-64 border-r border-aubergine/10 bg-white">
        <div className="px-6 py-5 border-b border-aubergine/10">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-full bg-aubergine flex items-center justify-center">
              <TRexLogo className="h-4 w-4" color="#E8D9B8" />
            </div>
            <div>
              <div className="text-sm font-medium text-aubergine">T-REX Admin</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-aubergine/45">Console</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive ? "bg-champagne/10 text-aubergine font-medium" : "text-aubergine/65 hover:text-aubergine hover:bg-aubergine/5"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-aubergine/10 p-3 space-y-2">
          <a href="/" target="_blank" rel="noreferrer" data-testid="admin-view-public-site" className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-aubergine/65 hover:text-aubergine hover:bg-aubergine/5">
            <ExternalLink className="h-4 w-4" /> Public site
          </a>
          <div className="rounded-md p-3 bg-bone/60 border border-aubergine/10">
            <div className="text-[10px] font-mono uppercase tracking-[0.12em] text-aubergine/45 mb-1">Signed in</div>
            <div className="text-sm font-medium text-aubergine truncate" title={user?.email}>{user?.name || user?.email}</div>
            <div className="text-xs text-aubergine/55 truncate">{user?.role === "super_admin" ? "Super-admin" : "Admin"}</div>
            <button onClick={doLogout} data-testid="admin-logout-button" className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-md border border-aubergine/20 hover:bg-aubergine/5 px-3 py-1.5 font-mono text-[11px] tracking-[0.04em]">
              <LogOut className="h-3 w-3" /> Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 border-b border-aubergine/10 bg-white">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-full bg-aubergine flex items-center justify-center">
            <TRexLogo className="h-3.5 w-3.5" color="#E8D9B8" />
          </div>
          <span className="text-sm font-medium">T-REX Admin</span>
        </div>
        <button onClick={doLogout} data-testid="admin-mobile-logout" className="inline-flex items-center gap-1 text-xs text-aubergine/65">
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </header>

      <main className="lg:pl-64">
        <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
